import { useTranslations } from "@jugl-web/utils";
import React, { useMemo, useState } from "react";
import { BroadcastChannel } from "broadcast-channel";
import { BroadcastChannelName } from "@web-src/features/app/types";
import { getSessionId } from "@web-src/utils/session";
import {
  InteractiveContainer,
  OTPVerificationForm,
} from "@jugl-web/ui-components/cross-platform";
import { useVerifyOtpMutation } from "@web-src/features/auth/authApi";
import { useAppDispatch } from "@web-src/store";
import { proceedLogin } from "@web-src/features/auth/authSlice";
import isObject from "lodash/isObject";
import { JUGL_LOGIN, saveItemToStorage } from "@jugl-web/utils/storage";
import { AuthStep, LoginMethod, PhoneMethod } from "../../types";
import { useLoginPage } from "../../providers/LoginPageProvider/useLoginPage";

const channel = new BroadcastChannel(BroadcastChannelName.auth);

const OtpVerification: React.FC = () => {
  const {
    loginMethod,
    email,
    phone,
    phoneMethod,
    setAuthStep,
    id,
    requestOtpVerification,
  } = useLoginPage();
  const { t } = useTranslations();
  const emitterId = getSessionId();
  const dispatch = useAppDispatch();
  const [errorMessage, setErrorMessage] = useState("");
  const [verifyOtp, { isLoading }] = useVerifyOtpMutation();

  const handleSubmit = async (otp: string) => {
    if (isLoading) return;
    setErrorMessage("");
    const resp = await verifyOtp({
      id,
      otp,
    });
    if ("data" in resp) {
      const { token, refreshToken } = resp.data;
      channel.postMessage({ emitterId });
      const login = phone.isValid ? `${phone.code} ${phone.phone}` : email;
      saveItemToStorage(JUGL_LOGIN, login);
      dispatch(proceedLogin({ token, refreshToken, login }));
    } else if (
      "data" in resp.error &&
      isObject(resp.error.data) &&
      "errors" in resp.error.data &&
      typeof resp.error.data.errors === "string"
    ) {
      setErrorMessage(resp.error.data.errors);
    }
  };

  const { title, subTitle, buttonText } = useMemo(() => {
    if (loginMethod === LoginMethod.email) {
      return {
        title: t({
          id: "login-page.enter-code-sent-to-email",
          defaultMessage: "Enter Code Sent to Your Email",
        }),
        subTitle: t(
          {
            id: "login-page.we-sent-it-to-email",
            defaultMessage: "We sent it to the Email {email}",
          },
          {
            email: <span className="text-primary-800">{email}</span>,
          }
        ),
        buttonText: t({
          id: "login-page.change-email",
          defaultMessage: "Change Email Address",
        }),
      };
    }
    if (phoneMethod === PhoneMethod.sms) {
      return {
        title: t({
          id: "login-page.enter-code-sent-to-number",
          defaultMessage: "Enter Code Sent to Your Number",
        }),
        subTitle: t(
          {
            id: "login-page.we-sent-it-to-number",
            defaultMessage: "We sent it to the Phone Number {number}",
          },
          {
            number: (
              <span className="font-medium text-primary">{`${phone.code} ${phone.phone}`}</span>
            ),
          }
        ),
        buttonText: t({
          id: "login-page.change-number",
          defaultMessage: "Change Phone Number",
        }),
      };
    }
    return {
      title: t({
        id: "login-page.enter-code-sent-to-whatsapp",
        defaultMessage: "Enter Code Sent to Your WhatsApp Account",
      }),
      subTitle: t(
        {
          id: "login-page.we-sent-it-to-whatsapp",
          defaultMessage:
            "We sent it to the WhatsApp Account with number {number}",
        },
        {
          number: (
            <span className="font-medium text-primary">{`${phone.code}${phone.phone}`}</span>
          ),
        }
      ),
      buttonText: t({
        id: "login-page.change-number",
        defaultMessage: "Change Phone number",
      }),
    };
  }, [loginMethod, t, email, phone.code, phone.phone, phoneMethod]);

  return (
    <div className="flex h-full grow flex-col items-center gap-5 font-secondary">
      <div className="flex h-full flex-1 flex-col items-center">
        <OTPVerificationForm
          title={title}
          subTitle={
            <div className="flex flex-col items-center gap-[6px]">
              <span className="text-sm text-grey-800">{subTitle}</span>
              <InteractiveContainer
                className="w-max px-3 text-sm text-grey-900 underline transition-all duration-300 hover:font-medium hover:text-primary-800"
                onClick={() => setAuthStep(AuthStep.login)}
              >
                {buttonText}
              </InteractiveContainer>
            </div>
          }
          onSubmit={handleSubmit}
          onResend={requestOtpVerification}
          error={errorMessage}
        />
      </div>
    </div>
  );
};

export default OtpVerification;
